import { useServicesStore } from '../stores/services';

export function useUtils() {
    /**
     * Limpia el valor de name, devolviendo "" si es null o { "@nil": "true" }
     */
    function clearNill(name: string | { "@nil": string } | null | undefined): string {
        if (
            name === undefined ||
            name === null ||
            (typeof name === 'object' && name !== null && name['@nil'] === 'true')
        ) {
            return ''
        }
        return typeof name === 'string' ? name : ''
    }
    /**
     * Formatea un string UTC tipo ISO, "YYYY-MM-DD HH:mm:ss.SSS", "YYYY-MM-DDTHH:mm:ss.SSS" o solo fecha a "YYYY-MM-DD HH:mm:ss".
     * Si el valor es undefined, null, vacío o JSON nil, retorna "".
     */
    function formatUtc(
        utc: string | { "@nil": string } | null | undefined
    ): string {
        if (
            utc === undefined ||
            utc === null ||
            utc === '' ||
            (typeof utc === 'object' && utc !== null && utc['@nil'] === 'true')
        ) {
            return ''
        }
        if (typeof utc === 'string') {
            // "YYYY-MM-DDTHH:mm:ss.SSS" o "YYYY-MM-DD HH:mm:ss.SSS"
            let match = utc.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}:\d{2})/)
            if (match) {
                return `${match[1]} ${match[2]}`
            }
            // Solo fecha "YYYY-MM-DD"
            match = utc.match(/^(\d{4}-\d{2}-\d{2})$/)
            if (match) {
                return match[1]
            }
        }
        return ''
    }
    /**
     * Convierte metros a pies
     * @param meters - Valor en metros
     * @param decimals - Número de decimales (por defecto 2)
     * @returns Valor en pies
     */
    function metersToFeet(meters: number | string, decimals: number = 2): number {
        const metersNum = typeof meters === 'string' ? parseFloat(meters) : meters
        if (isNaN(metersNum)) return 0

        // 1 metro = 3.28084 pies
        const feet = metersNum * 3.28084
        return Number(feet.toFixed(decimals))
    }
    /**
     * Obtiene el nombre del archivo a partir de un path
     * @param path - El path del archivo
     * @returns El nombre del archivo
     */
    function getFileNameFromPath(path: string): string {
        // Dividir el path por el separador de directorios y devolver el último segmento
        return path.split(/\\|\//).pop() || '';
    }

    /**
     * Extrae la fecha y hora en formato UTC ISO a partir del nombre de un archivo.
     * @param filename - El nombre del archivo en el formato `YYYY-MM-DD_HH-MM-SS_PRIMARY`
     * @returns La fecha y hora en formato UTC ISO, como `YYYY-MM-DDTHH:mm:ssZ`
     * @throws Error si el nombre del archivo no contiene una fecha válida.
     */
    function extractUTCFromFilename(filename: string): string {
        // Extrae la parte de la fecha y hora del nombre del archivo
        const match = filename.match(/(\d{4}-\d{2}-\d{2})_(\d{2}-\d{2}-\d{2})/);
        if (!match) {
            throw new Error('El nombre del archivo no contiene una fecha válida en el formato esperado.');
        }

        // Combina la fecha y hora en formato ISO
        const [date, time] = [match[1], match[2].replace(/-/g, ':')];
        return `${date}T${time}Z`;
    }
    /**
 * Devuelve el mimeType a partir de la extensión de un archivo.
 * @param extension - La extensión del archivo (con o sin punto, en mayúsculas o minúsculas).
 * @returns El mimeType correspondiente o "application/octet-stream" si no se encuentra.
 */
    function getMimeTypeFromExtension(extension: string): string {
        // Mapeo de extensiones a mimeTypes
        const mimeTypes: Record<string, string> = {
            'jpg': 'image/jpeg',
            'jpeg': 'image/jpeg',
            'png': 'image/png',
            'gif': 'image/gif',
            'webp': 'image/webp',
            'svg': 'image/svg+xml',
            'pdf': 'application/pdf',
            'txt': 'text/plain',
            'html': 'text/html',
            'htm': 'text/html',
            'css': 'text/css',
            'js': 'application/javascript',
            'json': 'application/json',
            'xml': 'application/xml',
            'csv': 'text/csv',
            'zip': 'application/zip',
            'rar': 'application/vnd.rar',
            '7z': 'application/x-7z-compressed',
            'tar': 'application/x-tar',
            'gz': 'application/gzip',
            'mp3': 'audio/mpeg',
            'wav': 'audio/wav',
            'mp4': 'video/mp4',
            'avi': 'video/x-msvideo',
            'mov': 'video/quicktime',
            'mkv': 'video/x-matroska',
            'doc': 'application/msword',
            'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'xls': 'application/vnd.ms-excel',
            'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'ppt': 'application/vnd.ms-powerpoint',
            'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
        };

        // Normalizar la extensión (quitar el punto y convertir a minúsculas)
        const normalizedExtension = extension.replace(/^\./, '').toLowerCase();

        // Devolver el mimeType correspondiente o un valor por defecto
        return mimeTypes[normalizedExtension] || 'application/octet-stream';
    }
    /**
    * Detecta si la aplicación está siendo accedida desde un dispositivo móvil.
    * @returns {boolean} - `true` si es un dispositivo móvil, `false` en caso contrario.
    */
    function isMobileDevice(): boolean {
        const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
        return /android|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());
    }
    /**
     * Convierte un entero ARGB (como -16776961) a color CSS hexadecimal (#RRGGBB)
     * @param argb - Valor entero ARGB
     * @returns string color hexadecimal CSS
     */
    function intToHexColor(argb: number): string {
        const n = argb < 0 ? argb + 0x100000000 : argb;
        const rgb = n & 0xFFFFFF;
        return '#' + rgb.toString(16).padStart(6, '0');
    }

    /**
     * Devuelve el event_bg correspondiente a un event_type usando services.eventTypes
     * @param event_type string
     * @returns string | undefined
     */
    function getEventBg(event_type: string): string | undefined {
        const services = useServicesStore();
        const found = services.eventTypes.find(et => et.event_type === event_type);
        return found?.event_bg;
    }
    /**
     * Devuelve el event_type_id correspondiente a un event_type usando services.eventTypes
     * @param event_type string
     * @returns number 
     */
    function getEventTypeId(event_type: string): number {
        const services = useServicesStore();
        const found = services.eventTypes.find(et => et.event_type === event_type);
        return found ? found.event_type_id : 0;
    }

    return {
        clearNill,
        formatUtc,
        metersToFeet,
        getFileNameFromPath,
        extractUTCFromFilename,
        getMimeTypeFromExtension,
        isMobileDevice,
        intToHexColor,
        getEventBg,
        getEventTypeId
    }

}

    